/**
 * ProviderCoveragePage — `/dashboard/provider/coverage`.
 *
 * Phase 2 extraction: service_zips (comma-separated input) +
 * weekly availability grid. Saves via PATCH /profiles/provider —
 * the backend deep-merges nested dicts so sending only these
 * fields leaves pricing / business_name / description / commission
 * untouched.
 */
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const emptyAvailability = () =>
  DAYS.reduce((acc, d) => ({ ...acc, [d]: ["", ""] }), {});

export default function ProviderCoveragePage() {
  const { providerProfile, setProviderProfile, setProfile } = useAuth();
  const { requestAction, gateProps } = useImpersonationGate();
  const [form, setForm] = useState({
    service_zips: "",
    availability: emptyAvailability(),
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/profiles/provider/me");
        if (!active) return;
        setForm({
          service_zips: (data?.service_zips || []).join(", "),
          availability: { ...emptyAvailability(), ...(data?.availability || {}) },
        });
        setProviderProfile(data);
        setProfile(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [setProviderProfile, setProfile]);

  const updateAvail = (day, idx, v) =>
    setForm((s) => {
      const a = { ...s.availability };
      const pair = [...(a[day] || ["", ""])];
      pair[idx] = v;
      a[day] = pair;
      return { ...s, availability: a };
    });

  const handleSave = (e) => {
    e.preventDefault();
    requestAction("save coverage settings", async () => {
      setSaving(true);
      try {
        const payload = {
          service_zips: form.service_zips
            .split(",")
            .map((z) => z.trim())
            .filter(Boolean),
          availability: form.availability,
        };
        const { data } = await api.patch("/profiles/provider", payload);
        setProviderProfile(data);
        setProfile(data);
        setForm((s) => ({
          ...s,
          service_zips: (data?.service_zips || []).join(", "),
          availability: { ...s.availability, ...(data?.availability || {}) },
        }));
        toast.success("Coverage saved!");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setSaving(false);
      }
    });
  };

  // Display-only summary using the loaded providerProfile for header chips.
  const zipCount = (providerProfile?.service_zips || []).length;

  return (
    <div className="mx-auto max-w-6xl" data-testid="provider-coverage-page">
      <header className="mb-6">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Coverage
        </span>
        <h2 className="font-heading text-3xl font-bold text-pp-text">
          Service area &amp; hours
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {zipCount > 0
            ? `Currently serving ${zipCount} ZIP code${zipCount === 1 ? "" : "s"}.`
            : "Add ZIP codes to start receiving bookings."}
        </p>
      </header>

      <Card
        className="rounded-3xl border-border bg-pp-surface p-8 shadow-none"
        data-testid="provider-coverage-card"
      >
        <form
          onSubmit={handleSave}
          className="space-y-5"
          data-testid="provider-coverage-form"
        >
          {!loaded && (
            <p
              className="text-sm text-muted-foreground"
              data-testid="provider-coverage-loading"
            >
              Loading your coverage…
            </p>
          )}
          <fieldset disabled={!loaded} className="space-y-5 disabled:opacity-60">
            <div>
              <Label className="text-sm font-semibold text-pp-text">
                Service ZIPs (comma-separated)
              </Label>
              <Input
                value={form.service_zips}
                onChange={(e) =>
                  setForm((s) => ({ ...s, service_zips: e.target.value }))
                }
                className="mt-2 h-12 rounded-full px-5"
                placeholder="68114, 68022, …"
                data-testid="provider-service-zips-input"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Customers in these ZIPs can book with you.
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-muted p-5">
              <p className="mb-4 font-heading text-lg font-semibold text-pp-text">
                Weekly availability
              </p>
              <div className="space-y-3">
                {DAYS.map((day) => (
                  <div
                    key={day}
                    className="grid grid-cols-3 items-center gap-3"
                    data-testid={`provider-availability-row-${day}`}
                  >
                    <Label className="text-sm font-semibold capitalize text-pp-text">
                      {day}
                    </Label>
                    <Input
                      type="time"
                      value={form.availability[day]?.[0] || ""}
                      onChange={(e) => updateAvail(day, 0, e.target.value)}
                      className="h-10 rounded-full px-4"
                      data-testid={`provider-availability-${day}-start`}
                    />
                    <Input
                      type="time"
                      value={form.availability[day]?.[1] || ""}
                      onChange={(e) => updateAvail(day, 1, e.target.value)}
                      className="h-10 rounded-full px-4"
                      data-testid={`provider-availability-${day}-end`}
                    />
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Leave a day blank to mark it as unavailable.
              </p>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={saving || !loaded}
                className="h-12 rounded-full bg-pp-orange px-8 text-pp-cta-text hover:bg-pp-orange-hover"
                data-testid="provider-save-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save coverage"
                )}
              </Button>
            </div>
          </fieldset>
        </form>
      </Card>
      <ImpersonationConfirmGate {...gateProps} />
    </div>
  );
}
